import {aiChatWhitelineApiNew, whitelineApi} from '@/shared/api';
import {
  AiCarouselItem,
  AiProcessStatusRes,
  BudgetProposalWidget,
  ChatMessageType,
  ChatSession,
  CreateSessionRequest,
  FileUploadResponse, GetKpUrlResponse,
  InvoiceItem,
  SendMessageRequest
} from '@/features/AiChat/model/api/chat/types.ts';
import { AxiosError, AxiosResponse } from 'axios';
import { ExtendedFile } from '@/features/AiChat/model/context/types.ts';
import { toast } from '@/components/ui/sonner.tsx';

export const getChatSessionsApi = (orgId: string): Promise<ChatSession[]> => {
    return aiChatWhitelineApiNew.get(`sessions?org_id=${orgId}`).then((res: AxiosResponse) => {
        return res.data;
    }).catch((err: AxiosError) => {
      toast.error('Не получилось загрузить черновики');
      throw err;
    })
}

export const getChatSessionApi = (sessionId: number): Promise<ChatSession> => {
  return aiChatWhitelineApiNew.get(`sessions/${sessionId}`).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err: AxiosError) => {
    toast.error('Не получилось загрузить черновик');
    throw err;
  })
}

export const getChatSessionStatusApi = (sessionId: number): Promise<AiProcessStatusRes> => {
  return aiChatWhitelineApiNew.get(`status?session_id=${sessionId}`).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err: AxiosError) => {
    toast.error('Не получилось загрузить статус');
    throw err;
  })
}

export const deleteChatSessionApi = (sessionId: number): Promise<number> => {
  return aiChatWhitelineApiNew.delete(`/sessions/${sessionId}`).then(() => {
    return sessionId;
  }).catch((err: AxiosError) => {
    toast.error('Не удалось удалить поставку');
    throw err;
  })
}

export const createSessionApi = (payload: CreateSessionRequest): Promise<ChatSession> => {
  return aiChatWhitelineApiNew.post('sessions', payload).then((res: AxiosResponse) => {
    return res.data
  }).catch((err: AxiosError) => {
    toast.error('Не удалось создать поставку');
    throw err;
  })
}

export const uploadFileApi = (file: ExtendedFile, sessionId: number): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file.originFile);

  return aiChatWhitelineApiNew
    .post<FileUploadResponse>(`/files/upload?session_id=${sessionId}`, formData)
    .then((res: AxiosResponse<FileUploadResponse>) => {
      return res.data;
    })
    .catch((err) => {
      toast.error(`Не получилось загрузить файл ${file.originFile.name}`);
      throw err;
    });
}

export const postMessageApi = (body: SendMessageRequest): Promise<ChatMessageType> => {
  return aiChatWhitelineApiNew.post(`/messages`, body).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err) => {
    toast.error(`Не получилось отправить сообщение`);
    throw err;
  })
}

export const getInvoiceItemsApi = (sessionId: number): Promise<InvoiceItem[]> => {
  return aiChatWhitelineApiNew.get(`/sessions/${sessionId}/items`).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err) => {
    toast.error(`Не удалось загрузить распознанные товары`);
    throw err;
  })
}

export const getInvoiceItemApi = (sessionId: number, itemId: number): Promise<InvoiceItem> => {
  return aiChatWhitelineApiNew.get(`/sessions/${sessionId}/items/${itemId}`).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err) => {
    toast.error(`Не удалось загрузить товар`);
    throw err;
  })
}

export type UpdateInvoiceItemRequest = Partial<Omit<InvoiceItem, 'weight_gross_kg' | 'weight_net_kg'>> & {
  weight_gross_kg?: number | null;
  weight_net_kg?: number | null;
};

export const updateInvoiceItemApi = (sessionId: number, itemId: number, body: UpdateInvoiceItemRequest): Promise<InvoiceItem> => {
  return aiChatWhitelineApiNew.patch(`/sessions/${sessionId}/items/${itemId}`, body)
    .then((res: AxiosResponse) => {
      return res.data;
    }).catch((err) => {
      toast.error(`Не получилось обновить товар`);
      throw err;
    })
}

export const deleteInvoiceItemApi = (sessionId: number, itemId: number): Promise<number> => {
  return aiChatWhitelineApiNew.delete(`/sessions/${sessionId}/items/${itemId}`).then((res: AxiosResponse) => {
    return itemId;
  }).catch((err) => {
    toast.error(`Не получилось удалить товар`);
    throw err;
  })
}

export const getWidgetProposalApi = (sessionId: number): Promise<BudgetProposalWidget> => {
  return aiChatWhitelineApiNew.get(`/sessions/${sessionId}/proposal`).then((res: AxiosResponse) => {
    return res.data;
  }).catch((err) => {
    toast.error(`Не получилось загрузить бюджет`);
    throw err;
  })
}

export const getKpUrl = async (id: string): Promise<GetKpUrlResponse> => {
  const response = await whitelineApi.get<GetKpUrlResponse>(
    `api/v1/shipments/${id}/commercial-proposal`
  );

  return response.data;
};

export const getAiCarouselApi = (): Promise<AiCarouselItem[]> => {
  return aiChatWhitelineApiNew.get(`/carousel`).then((res: AxiosResponse) => {
    return res.data.stages;
  }).catch((err) => {
    throw err;
  })
}

export const downloadFullReportApi = (file_id: number): Promise<void> => {
  return aiChatWhitelineApiNew
  .get(`/files/${file_id}`, {
    responseType: 'blob',
  })
  .then((res: AxiosResponse<Blob>) => {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `report_${file_id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  })
  .catch((err) => {
    toast.error(`Не удалось скачать отчет`);
    throw err;
  });
};